import React, { useState, useEffect } from 'react';
import { Card, Tag, Empty, Spin, Tabs } from 'antd';
import { Bell, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { ACTIVE_ALERTS, ALERT_HISTORY } from '../../constants/api';
import { ALERT_SEVERITY_COLORS, ALERT_TYPE_LABELS } from '../../constants/enum';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const SEVERITY_ICONS = {
  critical: <AlertTriangle size={16} className="text-red-500" />,
  high: <AlertCircle size={16} className="text-orange-500" />,
  medium: <Info size={16} className="text-yellow-500" />,
  low: <Info size={16} className="text-blue-500" />,
  info: <Info size={16} className="text-muted-foreground" />,
};

const SEVERITY_STYLES = {
  critical: 'border-rose-200 dark:border-rose-500/30 from-rose-50/80 dark:from-rose-950/40',
  high: 'border-orange-200 dark:border-orange-500/30 from-orange-50/80 dark:from-orange-950/40',
  medium: 'border-amber-200 dark:border-amber-500/30 from-amber-50/80 dark:from-amber-950/40',
  low: 'border-sky-200 dark:border-sky-500/30 from-sky-50/80 dark:from-sky-950/40',
  info: 'border-border from-muted/40',
};
const SEVERITY_BAR = {
  critical: 'from-rose-400 to-rose-600',
  high: 'from-orange-400 to-orange-600',
  medium: 'from-amber-400 to-amber-600',
  low: 'from-sky-400 to-sky-600',
  info: 'from-slate-400 to-slate-600',
};

const AlertCard = ({ alert }) => {
  const sevStyle = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.info;
  const sevBar = SEVERITY_BAR[alert.severity] || SEVERITY_BAR.info;
  return (
    <div className={`relative flex items-start gap-3 p-4 rounded-xl border bg-gradient-to-r to-transparent mb-3 overflow-hidden ${sevStyle}`}>
      <span className={`absolute inset-y-0 left-0 w-1 bg-gradient-to-b ${sevBar} rounded-l-xl`} />
      <div className="mt-0.5 pl-1">
        {SEVERITY_ICONS[alert.severity] || <Bell size={16} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-foreground">{alert.title}</span>
          <Tag color={ALERT_SEVERITY_COLORS[alert.severity] || '#6b7280'}>
            {alert.severity?.toUpperCase()}
          </Tag>
          {alert.type && (
            <Tag>{ALERT_TYPE_LABELS[alert.type] || alert.type}</Tag>
          )}
        </div>
        <p className="text-sm text-foreground/80 mt-1">{alert.message}</p>
        <p className="text-xs text-muted-foreground mt-2">
          {alert.created_at ? dayjs(alert.created_at).fromNow() : ''}
        </p>
      </div>
    </div>
  );
};

const AlertsList = () => {
  const { fetch } = useApiMethods();
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [activeRes, historyRes] = await Promise.all([
        fetch(ACTIVE_ALERTS, {}, setLoading),
        fetch(ALERT_HISTORY, {}),
      ]);
      if (activeRes?.status !== 'fail') {
        setActiveAlerts(activeRes?.data || activeRes?.results || []);
      }
      if (historyRes?.status !== 'fail') {
        setHistory(historyRes?.data || historyRes?.results || []);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  const tabs = [
    {
      key: 'active',
      label: `Active (${activeAlerts.length})`,
      children:
        activeAlerts.length > 0 ? (
          activeAlerts.map((alert, i) => <AlertCard key={alert.id || i} alert={alert} />)
        ) : (
          <Empty description="No active alerts" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ),
    },
    {
      key: 'history',
      label: 'History',
      children:
        history.length > 0 ? (
          history.map((alert, i) => <AlertCard key={alert.id || i} alert={alert} />)
        ) : (
          <Empty description="No alert history" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ),
    },
  ];

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen text-foreground">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Alerts</h2>
        <p className="text-muted-foreground text-sm">System alerts and notifications</p>
      </div>

      <Tabs items={tabs} defaultActiveKey="active" />
    </div>
  );
};

export default AlertsList;
